import NiceModal from '@ebay/nice-modal-react';
import { DefaultRenderer, MarkerClusterer } from '@googlemaps/markerclusterer';

import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import AddressInput from 'components/field/AddressInput';
import Button from 'components/field/Button';
import Maps from 'components/maps/Maps';
import { getGeocodePosition, getPlaceDetails, getWidgetContainer } from 'components/maps/utils/mapHelpers';

import useAlert from 'hooks/useAlert';

import AddOrderForm from 'pages/Shipments/Shipments/OrderForm/AddOrderForm';

import { STORAGE_PUBLIC } from 'services/consts';

import { setListMapView } from 'stores/layoutSlice';

import AppLogger from 'utils/AppLogger';

import { faList, faLoader, faPlus, faSync } from '@fortawesome/pro-solid-svg-icons';

import DynamicListContextMenu from '../DynamicListContextMenu';
import * as S from './DynamicListMapView.styled';
import InfoWindow from './InfoWindow';
import { DOM_READY, LEFT_CLICK, RIGHT_CLICK } from './events';

const DynamicListMapView = ({ data, filterModel, columns, setLatLng, loading, actionsMenu }) => {
	const maps = window?.google?.maps;
	const iconSize = maps && new maps.Size(40, 40);

	const { t } = useTranslation();
	const { showError } = useAlert();

	// dynamic list states
	const [anchorPoint, setAnchorPoint] = useState({ x: 0, y: 0 });
	const [isContextMenuOpen, setIsContextMenuOpen] = useState(false);
	const [selectedContextMenuRow, setSelectedContextMenuRow] = useState(null);

	// map states
	const [inputValue, setInputValue] = useState('');
	const [map, setMap] = useState(null);
	const [globalRedraw, setGlobalRedraw] = useState(null);
	const [mapIdle, setMapIdle] = useState(0);
	const [isScanning, setIsScanning] = useState(false); // is scanning for new markers
	const [clusterInstance, setClusterInstance] = useState(null); // cluster instance
	const [, setHoveredItem] = useState(null); // hovered item when mouse over marker
	const [infoWindowContent, setInfoWindowContent] = useState(null); // info window content
	const [scanDisabled, setScanDisabled] = useState(false); // info window content

	const infoWindow = useRef(); // info window ref

	const [rowMenu, setRowMenu] = useState();

	const handleOnContextMenu = async ({ x, y, item, event, latLng }) => {
		setAnchorPoint({ x, y });
		setIsContextMenuOpen(true);
		item && setSelectedContextMenuRow(item);

		switch (event) {
			case RIGHT_CLICK:
				setRowMenu([
					{
						items: [
							{
								icon: faLoader,
								label: 'loading',
								action: () => {},
								disabled: () => true,
								footerItem: true
							}
						]
					}
				]);

				setRowMenu([
					{
						items: [
							{
								icon: faPlus,
								label: 'addOrder',
								action: async () => {
									const address = await getGeocodePosition({
										lat: latLng?.lat(),
										lng: latLng?.lng()
									});

									NiceModal.show(AddOrderForm, {
										address: {
											...address,
											cityName: address.city,
											countryName: address.country
										}
									});
								},
								footerItem: true
							}
						]
					}
				]);
				return;
			case LEFT_CLICK:
				setRowMenu(actionsMenu);
				return;
		}
	};

	const closeSelected = () => {
		setIsContextMenuOpen(false);
		setSelectedContextMenuRow(null);
	};

	const onDataChange = async () => {
		if (globalRedraw === null) {
			setTimeout(onDataChange, 200);
			return;
		}
		const bounds = new maps.LatLngBounds();

		try {
			const dataList = data.filter((a) => a.lat && a.lng);

			let nonDrawed = dataList.filter((item) => {
				if (globalRedraw) return true;

				return !clusterInstance?.markers?.find(({ ID }) => ID === item.ID);
			});

			const beRemoved = clusterInstance?.markers?.filter((item) => {
				if (globalRedraw) return false;
				return !dataList.find(({ ID }) => ID === item.ID);
			});

			const { AdvancedMarkerElement } = await maps.importLibrary('marker');

			nonDrawed = nonDrawed?.map((item) => {
				const { lat, lng, ID } = item;

				const markerImage = document.createElement('img');

				markerImage.src = `${STORAGE_PUBLIC}/mapview-icons/order2.svg`;

				markerImage.style.width = `${iconSize.width}px`;

				const marker = new AdvancedMarkerElement({
					position: { lat: parseFloat(lat), lng: parseFloat(lng) },
					content: markerImage,
					id: ID,
					zIndex: 0
				});

				marker.content.classList.add('bounce');

				const openInfoWindow = () => {
					infoWindow.current.setContent(infoWindowContainer);
					if (infoWindowContainer.parentElement) {
						infoWindowContainer.parentElement.style = {};
					}

					infoWindow.current.open({
						anchor: marker,
						map
					});
				};

				marker.addListener('click', () => {
					setHoveredItem(marker);
					setInfoWindowContent(item);
					setIsContextMenuOpen(false);

					openInfoWindow();
				});

				return marker;
			});

			// first time draw
			if (!clusterInstance || globalRedraw) {
				// custom renderer cluster
				const markerRenderer = new DefaultRenderer();
				markerRenderer.render = function ({ count, position }) {
					const url =
						count < 30
							? `${STORAGE_PUBLIC}/mapview-icons/low.svg`
							: count > 60
								? `${STORAGE_PUBLIC}/mapview-icons/high.svg`
								: `${STORAGE_PUBLIC}/mapview-icons/mid.svg`;
					const size = count < 30 ? 30 : count > 60 ? 36 : 32;

					const markerImage = document.createElement('div');

					markerImage.innerHTML = `
					<div style="position: relative;">
						<img src=${url} style="width: 100%; height: 100%;"></img>
						<span style="position: absolute; top: 45%; left: 50%; transform: translate(-50%, -50%); color: white; font-weight: bold;">${count}</span>
					</div>
					`;

					markerImage.style.width = `${size}px`;

					const clusterMarker = new AdvancedMarkerElement({
						position,
						content: markerImage,
						// adjust zIndex to be above other markers
						zIndex: Number(maps.Marker.MAX_ZINDEX) + count,
						title: `${t('mapview.order.clusterCount')} ${count}`
					});
					return clusterMarker;
				};

				// create cluster instance
				const cluster = new MarkerClusterer({
					map,
					markers: nonDrawed,
					renderer: markerRenderer
				});
				setClusterInstance(cluster);
			} else {
				beRemoved?.forEach((item) => {
					clusterInstance.removeMarker(item);
				});
				nonDrawed?.forEach((marker) => {
					clusterInstance.addMarker(marker);
				});
			}

			if (dataList?.length > 1) {
				dataList.forEach((item) => {
					const { lat, lng } = item;
					bounds.extend({ lat: parseFloat(lat), lng: parseFloat(lng) });
				});
				const orderFilter = filterModel.find((filter) => filter.column.accessor === 'orderID' && filter.value);
				if (orderFilter) {
					const point = data.find((item) => item.orderID === orderFilter.value);
					if (point && point.lat && point.lng) {
						map.setZoom(14);
						setTimeout(() => map.panTo({ lat: point.lat, lng: point.lng }), 500);
					}
				} else {
					map.fitBounds(bounds);
					map.setCenter(bounds.getCenter());
				}
			} else if (dataList?.length) {
				const { lat, lng } = dataList[0];
				const latlng = new maps.LatLng(lat, lng);

				map.setZoom(7);
				map.panTo(latlng);
			}
		} catch (error) {
			AppLogger.error(error);
			showError({
				header: 'error',
				message: error.response.data.code
			});
		} finally {
			setIsScanning(false);
		}
	};

	useEffect(() => {
		onDataChange();
	}, [data]);

	const drawBoundMarkers = async ({ redraw }) => {
		const b = map.getBounds();
		if (!b) return;

		const { south, west, north, east } = b.toJSON();
		setGlobalRedraw(redraw);
		setLatLng({ lat: { from: south, to: north }, lng: { from: west, to: east } });

		setScanDisabled(true);
		setIsScanning(true);
	};

	/** Containers for map control items*/
	const buttonContainer = useMemo(() => getWidgetContainer(), []);
	const listViewContainer = useMemo(() => {
		const element = document.createElement('div');
		element.style.marginBottom = '2rem';
		return element;
	}, []);

	const infoWindowContainer = useMemo(() => getWidgetContainer('100%', false, true), []);

	const onMapIdle = () => {
		map.controls[maps.ControlPosition.TOP].push(buttonContainer);
		map.controls[maps.ControlPosition.BOTTOM].push(listViewContainer);

		// create info window
		infoWindow.current = new maps.InfoWindow({
			content: ''
		});
		infoWindow.current.addListener(DOM_READY, () => {
			// hide close icon on info window
			const closeIcon = document.getElementsByClassName('gm-ui-hover-effect');
			closeIcon[0].style.display = 'none';
		});

		// add event listeners
		map.addListener('dragstart', () => {
			infoWindow.current.setMap(null);
			setIsContextMenuOpen(false);
			setScanDisabled(false);
		});
		map.addListener('zoom_changed', () => {
			// Close info window when zooming
			infoWindow.current.setMap(null);
			setIsContextMenuOpen(false);

			// reset hovered item
			setHoveredItem(null);
			setScanDisabled(false);
		});

		map.addListener('click', () => {
			setHoveredItem(null);
			infoWindow.current.setMap(null);
			setIsContextMenuOpen(false);
		});

		maps.event.addListener(map, RIGHT_CLICK, (e) => {
			setTimeout(() => {
				handleOnContextMenu({
					x: e.domEvent.clientX,
					y: e.domEvent.clientY,
					event: RIGHT_CLICK,
					latLng: e.latLng
				});
			}, 0);
		});

		setMapIdle((prev) => prev + 1); // every time map idle, increase mapIdle state for re-render
	};

	const onPlaceChange = (place, text) => {
		if (!place) return;
		setInputValue(text ?? place.formatted_address);

		if (place.geometry?.viewport) {
			const viewport = place.geometry.viewport;
			map.setZoom(14);
			map.fitBounds(new maps.LatLngBounds(viewport.getSouthWest(), viewport.getNorthEast()));
		} else if (place.name) {
			const [lat, lng] = place.name.split(' ').length > 1 ? place.name.split(' ') : place.name.split(',');

			if (!lat || !lng) return;

			if (isNaN(Number(lat)) || isNaN(Number(lng))) return;

			getPlaceDetails({ lat, lng }).then((newPlace) => {
				const viewport = newPlace?.geometry?.viewport;
				if (viewport) map.fitBounds(viewport);
			});
		} else {
			AppLogger.error("MapField: Can't find address_components or geometry => ", inputValue);
		}
	};

	useEffect(() => {
		if (map && maps) {
			maps.event.addListenerOnce(map, 'idle', () => {
				onMapIdle();
			});
		}

		return () => {
			clusterInstance?.clearMarkers?.();
			setClusterInstance(null);
		};
	}, [map]);

	useEffect(() => {
		if (mapIdle) drawBoundMarkers({ redraw: true });
	}, [mapIdle]);

	const dispatch = useDispatch();

	return (
		<S.Container>
			<S.MapContainer>
				<S.MapWrapper>
					<Maps setMap={setMap} zoom={3} />
				</S.MapWrapper>
				{!loading && isContextMenuOpen && (
					<DynamicListContextMenu
						menuItems={rowMenu}
						row={selectedContextMenuRow}
						anchorPoint={anchorPoint}
						onClose={() => closeSelected()}
						isContextMenuOpen={isContextMenuOpen}
						setIsContextMenuOpen={setIsContextMenuOpen}
					/>
				)}
				{createPortal(
					<AddressInput
						value={inputValue}
						setInputValue={(val) => setInputValue(val)}
						placeholder={t('enterAlocation')}
						formElement={false}
						onPlaceChange={onPlaceChange}
					/>,
					buttonContainer
				)}
				{createPortal(
					<div>
						<Button
							onClick={() => {
								infoWindow.current.setMap(null);
								drawBoundMarkers({ redraw: false });
							}}
							icon={faSync}
							loading={isScanning}
							label="scanArea"
							style={{ width: '120px', marginBottom: '1rem' }}
							disabled={scanDisabled}
						/>
					</div>,
					listViewContainer
				)}
				{createPortal(
					<S.ListViewButton>
						<Button
							icon={faList}
							onClick={() => dispatch(setListMapView(false))}
							variant="blue"
							label="listView"
							style={{ width: '120px' }}
						/>
					</S.ListViewButton>,
					listViewContainer
				)}

				{createPortal(
					<InfoWindow
						columns={columns.filter((column) => column.showOnMap)}
						hoveredMarker={infoWindowContent}
						onContextMenu={(e) =>
							handleOnContextMenu({ x: e.pageX, y: e.pageY, item: infoWindowContent, event: LEFT_CLICK })
						}
					/>,
					infoWindowContainer
				)}
			</S.MapContainer>
			<S.BottomContainer />
		</S.Container>
	);
};

export default DynamicListMapView;
